<template>
  <div
    :class="[
      'MenuItemCmp',
      { 'd-flex justify-content-center align-items-center': !hasSubmenu },
    ]"
  >
    <template v-if="!hasSubmenu">
      <slot name="link" />

      <svg v-if="mdAndDown" class="icon-caret-down">
        <use href="#icon-arrow-right" />
      </svg>
    </template>
    <template v-else>
      <button
        class="d-flex justify-content-between align-items-center"
        role="button"
        :aria-expanded="isOpenSubmenu ? 'true' : 'false'"
        :aria-controls="submenuId"
        @click="toggleSubMenu"
      >
        {{ dataSubmenuButtonLabel }}

        <svg v-if="mdAndDown" class="icon-caret-down">
          <use href="#icon-caret-down" />
        </svg>
      </button>
    </template>
    <template v-if="hasSubmenu">
      <div
        :id="submenuId"
        ref="submenuPanel"
        :class="['fatmenu-panel', { 'shadow-soft': lgAndUp }]"
        inert
        :aria-hidden="!isOpenSubmenu"
      >
        <div class="container-fluid subcontainer">
          <div class="row">
            <div v-if="lgAndUp" class="col-3">
              <slot name="link-intro" />
              <slot name="link-cta" />
            </div>
            <div class="col-12 col-lg-6 ps-lg-s pt-xs pt-lg-0">
              <slot name="subitems" />
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { castPropToType } from '@/helpers/cms-support';
import { xsOnly, mdAndUp, mdAndDown, lgAndUp } from '@/helpers/breakpoints.js';
export default {
  name: 'MenuItemCmp',
  props: {
    dataHasSubmenu: {
      type: [Boolean, String],
      default: false,
    },
    dataSubmenuButtonLabel: {
      type: String,
      default: '',
    },
    dataSubmenuDescription: {
      type: String,
      default: '',
    },
  },
  setup() {
    return { castPropToType, xsOnly, mdAndUp, mdAndDown, lgAndUp };
  },
  data() {
    return {
      isOpenSubmenu: false,
      submenuId: `submenu-${Math.random().toString(36).substr(2, 9)}`,
    };
  },
  computed: {
    hasSubmenu() {
      return this.dataHasSubmenu
        ? this.castPropToType(this.dataHasSubmenu, 'boolean')
        : false;
    },
  },
  mounted() {
    window.addEventListener('close-all-submenus', this.closeSubMenu);
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    window.removeEventListener('close-all-submenus', this.closeSubMenu);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggleSubMenu() {
      const panel = this.$refs.submenuPanel;
      if (!panel) return;

      const isOpen = panel.classList.contains('open');
      const noOneWasOpen =
        document.querySelectorAll('.fatmenu-panel.open').length == 0;

      // rimane il dispatch qui
      window.dispatchEvent(new CustomEvent('close-all-submenus'));

      // se sto aprendo e c'era già un pannello aperto,
      // ri-applico subito il lock sul pannello che sto per aprire
      if (!isOpen && !noOneWasOpen && lgAndUp.value) {
        this.$_a11Support_disableScrollOuterElement({
          $el: panel,
          enable: true,
        });
      }

      if (!isOpen) {
        const openPanel = () => {
          panel.classList.add('open');
          panel.removeAttribute('inert');
          if (lgAndUp.value) {
            this.$_a11Support_disableScrollOuterElement({
              $el: panel,
              enable: true,
            });
          }
        };

        if (noOneWasOpen) {
          openPanel();
        } else {
          setTimeout(() => {
            openPanel();
          }, 300);
        }
        this.isOpenSubmenu = true;
      } else {
        panel.classList.remove('open');
        panel.setAttribute('inert', '');
        this.isOpenSubmenu = false;

        setTimeout(() => {
          const openPanelsNow = document.querySelectorAll('.fatmenu-panel.open').length;
          if (openPanelsNow === 0) {
            this.$_a11Support_clearAllBodyScrollLocks();
          }
        }, 300);
      }
    },
    closeSubMenu() {
      const panel = this.$refs.submenuPanel;
      if (panel) {
        panel.classList.remove('open');
        panel.setAttribute('inert', '');
        this.isOpenSubmenu = false;
        this.$_a11Support_clearAllBodyScrollLocks();
      }
    },
    handleClickOutside(event) {
      const panel = this.$refs.submenuPanel;
      if (!panel || !panel.classList.contains('open')) return;
      if (!panel.contains(event.target) && !this.$el.contains(event.target)) {
        this.closeSubMenu();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-caret-down {
  display: none;
}
// header-navbar è sul template dell'header - serve per dare questi stili solo se si è nell'header poiche questi elementi sono anche usati nel footer
.header-navbar .MenuItemCmp {
  background: transparent;
  border-bottom: 1px solid $color-grey-400-50;
  width: 100%;
  @include media-breakpoint-down(lg) {
    position: relative;
    width: 100%;
  }
  @include media-breakpoint-up(lg) {
    border-bottom: none;
    height: 100%;
    padding-right: 16px;
  }
  @include media-breakpoint-up(xl) {
    padding-right: 48px;
  }

  &::v-deep > a,
  &::v-deep > button {
    width: 100%;
    position: relative;
    display: block;
    padding: 16px 0;
    color: $color-white;
    text-decoration: none;
    transition: color 0.2s ease-out;
    &:hover,
    &:active,
    &.is-active {
      color: $color-grey-400-75;
    }
    @include responsive-typography-properties($font-primary, a-support);

    @include media-breakpoint-up(lg) {
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-image: linear-gradient($color-white, $color-white);
        background-size: 0 100%, auto;
        background-repeat: no-repeat;
        background-position: left bottom;
        transition: all 0.2s ease-out;

        @include media-breakpoint-up(lg) {
          background-position: center bottom;
        }
      }

      &:hover,
      &:focus,
      &:active {
        color: $color-grey-400-75;
        // se da fastidio l'outline globale
        //outline: 0px solid !important;

        &::after {
          background-image: linear-gradient(
            $color-grey-400-75,
            $color-grey-400-75
          );
          background-size: 100% 100%, auto;
        }
      }

      &.is-active {
        color: $color-grey-200-50;

        &::after {
          background-image: linear-gradient(
            $color-grey-900-75,
            $color-grey-900-75
          );
          background-size: 100% 100%, auto;
        }
      }
    }
  }
  .icon-caret-down {
    display: block;
    width: 16px;
    height: 16px;
    color: white;
    position: absolute;
    right: 0px;
    top: 0px;
    top: 50%;
    transform: translateY(-50%);
  }
  .fatmenu-panel {
    position: relative;
    color: white;
    max-height: 0px;
    width: 100%;
    overflow: hidden;
    opacity: 0;
    transition: max-height 0.7s ease, opacity 0.7s ease;

    &.open {
      opacity: 1;
      max-height: 70vh;
      overflow: auto;
    }

    .subcontainer {
      padding-left: 0px !important;
      padding-right: 0px !important;
      .row {
        max-width: 98%;
      }
    }
  }
  .fatmenu-panel.open {
    overflow: auto;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE/Edge */
  }

  .fatmenu-panel.open::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @include media-breakpoint-up(lg) {
    .fatmenu-panel {
      position: absolute;
      top: 100%;
      color: black;
      background: white;
      max-height: 0px;
      left: 0px;
      width: 100%;
      overflow: hidden;
      opacity: 0;
      z-index: -1;
      padding-top: 50px;
      margin-top: -50px;
      transition: max-height 0.3s ease, opacity 0.5s ease;
      border-bottom-left-radius: 8px;
      border-bottom-right-radius: 8px;
      &.open {
        opacity: 1;
        max-height: calc(100vh - 50px); /* 50px è il padding-top */
        //min-height: 100vh;
      }

      .subcontainer {
        margin-top: -50px;
        padding: 32px !important;
        max-height: calc(100vh - 50px); /* 50px è il padding-top */
        padding-top: 82px !important;
        display: flex;
        .row {
          max-width: 100%;
          flex: 1;
          min-height: 0px;
          & > * {
            max-height: 100%;
            overflow: auto;
          }
        }
      }
    }
  }
  @include media-breakpoint-up(xl) {
    .fatmenu-panel {
      .subcontainer {
        padding: 40px !important;
        padding-top: 90px !important;
      }
    }
  }
}
</style>
